import { useMemo } from "react";
import { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import {
  getPieChartData,
  updateCoinRatios,
} from "@shared/features/portfolio/utils";
import {
  selectDisplayCurrency,
  selectIsProcessingQuickConnect,
  selectWorkspace,
} from "@shared/slices/common.slice";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import {
  ClientStatus,
  ConsolidatedAccountData,
  ConsolidatedCoinData,
  LanguageNameSpaces,
  ProcessedCoinExchangesData,
  WorkspaceType,
} from "@shared/types";
import { get, sumBy } from "lodash";
import { sharedLib } from "@shared/services/shared.service";
import {
  selectConsolidatedAccountsPortfolioData,
  selectPortfolioDataLoadingStates,
  selectPreprocessedPortfolioData,
} from "@shared/slices/portfolio.slice";
import {
  filterByClients,
  filterByStatus,
} from "@shared/features/filters/utils";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { PortfolioAccountData } from "@shared/features/portfolio/types";
import { useClientsData } from "@shared/utils/hooks/misc";
import { toFixed } from "@shared/utils/numbers";
import { skipToken } from "@reduxjs/toolkit/query";
import { isContractsSymbol } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface usePortfolioPieChartProps<
  T extends
    | ConsolidatedAccountData
    | ConsolidatedCoinData
    | ProcessedCoinExchangesData
> {
  dataSelector: (state: any) => T[];
  nameHeader: keyof T;
  filterDependencies?: boolean;
}

export const usePortfolioPieChart = <
  T extends
    | ConsolidatedAccountData
    | ConsolidatedCoinData
    | ProcessedCoinExchangesData
>({
  dataSelector,
  nameHeader,
}: usePortfolioPieChartProps<T>) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const theme = useTheme();
  const data = useSelector(dataSelector);

  const isCoinExchangeData = !!data.length && !("total_in_usd" in data[0]);
  const usdTotalPath = useMemo(
    () =>
      isCoinExchangeData ? ["total", DISPLAY_CURRENCIES.USD] : "total_in_usd",
    [isCoinExchangeData]
  );
  const btcTotalPath = useMemo(
    () =>
      isCoinExchangeData ? ["total", DISPLAY_CURRENCIES.BTC] : "total_in_btc",
    [isCoinExchangeData]
  );
  const totalDisplayCurrencyPath =
    displayCurrency === DISPLAY_CURRENCIES.BTC ? btcTotalPath : usdTotalPath;

  const marginDisplayCurrencyPath = useMemo(() => {
    return ["margin", displayCurrency === DISPLAY_CURRENCIES.BTC ? 1 : 2];
  }, [displayCurrency]);

  const filteredData = useMemo(
    () =>
      data.filter(
        (item) => !("symbol" in item) || !isContractsSymbol(item.symbol)
      ),
    [data]
  );

  const positionsData = useMemo(
    () =>
      data.filter((coin) => "symbol" in coin && isContractsSymbol(coin.symbol)),
    [data]
  );

  const pieChartData = useMemo(
    () => getPieChartData(theme, nameHeader, filteredData, usdTotalPath),
    [theme, filteredData, usdTotalPath, nameHeader]
  );

  const positionsPieChartData = useMemo(
    () => getPieChartData(theme, nameHeader, positionsData, "margin[2]"),
    [theme, positionsData, nameHeader]
  );

  const total = useMemo(() => {
    return filteredData.reduce((total, coinData) => {
      total += get(coinData, totalDisplayCurrencyPath, 0);
      return total;
    }, 0);
  }, [filteredData, totalDisplayCurrencyPath]);

  const margin = useMemo(() => {
    return positionsData.reduce((totalMargin, coinData) => {
      totalMargin += get(coinData, marginDisplayCurrencyPath, 0);
      return totalMargin;
    }, 0);
  }, [positionsData, marginDisplayCurrencyPath]);

  return {
    pieChartData,
    total,
    margin,
    positionsPieChartData,
  };
};

export const usePortfolioAccountsData = () => {
  const isProcessingQuickConnect = useSelector(selectIsProcessingQuickConnect);

  const getPortfolioDataQuery =
    sharedLib.sharedApi.portfolio.useGetPortfolioDataQuery(
      isProcessingQuickConnect ? skipToken : undefined
    );
  const getClientsListQuery =
    sharedLib.sharedApi.configuration.useGetClientsListQuery();
  const getPortfolioAmountQuery =
    sharedLib.sharedApi.portfolio.useGetPortfolioAmountQuery();
  const portfolioData = useSelector(selectConsolidatedAccountsPortfolioData);
  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();
  const { loading: portfolioDataFetching } = useSelector(
    selectPortfolioDataLoadingStates
  );
  const statusFilter = useSelector(
    portfolioFilters.selectors.selectStatusFilter
  );
  const clientsFilter = useSelector(
    portfolioFilters.selectors.selectClientsFilter
  );
  const { clientsData, getAccountById } = useClientsData();
  const amountsData = getPortfolioAmountQuery.data;

  const portfolioAccountIds = useMemo(() => {
    return new Set(portfolioData.map((account) => account.client_id));
  }, [portfolioData]);

  const isLoading =
    getClientsListQuery.isLoading || getPortfolioAmountQuery.isLoading;
  const allData: PortfolioAccountData[] = useMemo(() => {
    if (isLoading) return [];
    const allData: PortfolioAccountData[] = [...portfolioData]
      .map((account) => {
        const accountData = getAccountById(account.client_id);
        return { ...account, ...accountData };
      })
      .filter((account) => !!account.client_status);
    const filteredAccounts = clientsData?.filter(
      (account) => account.client_status !== ClientStatus.DELETED
    );
    const activeAccounts = filteredAccounts?.filter(
      (account) => account.client_status === ClientStatus.ACTIVE
    );
    const totalUsd = sumBy(
      activeAccounts,
      (account) => amountsData?.[account.client_id] || 0
    );
    filteredAccounts?.forEach((account) => {
      if (!portfolioAccountIds.has(account.client_id)) {
        const isValid = account.client_status === ClientStatus.ACTIVE;
        const accountTotalUsd = amountsData?.[account.client_id] || 0;
        allData.push({
          total_in_btc: 0,
          total_in_usd: isValid ? accountTotalUsd : 0,
          available_in_btc: 0,
          available_in_usd: 0,
          total_ratio: isValid ? accountTotalUsd / totalUsd : 0,
          client: account.client_name,
          ...account,
          isLoading: isValid && portfolioDataFetching,
        });
      }
    });

    return filterByClients(
      filterByStatus(allData, statusFilter),
      clientsFilter
    );
  }, [
    clientsData,
    portfolioData,
    statusFilter,
    clientsFilter,
    isLoading,
    portfolioDataFetching,
    amountsData,
  ]);

  const refetch = () => {
    try {
      getPortfolioAmountQuery.refetch();
      getClientsListQuery.refetch();
      getPortfolioDataQuery.refetch();
    } catch {}
  };

  const getNoCachePortfolioData = () => {
    getPortfolioData({ use_cache: false });
  };

  return {
    portfolioData: allData,
    isLoading:
      getPortfolioAmountQuery.isLoading || getClientsListQuery.isLoading,
    isLoadingPortfolioData: getPortfolioDataQuery.isLoading,
    isFetching:
      getPortfolioAmountQuery.isFetching ||
      getClientsListQuery.isFetching ||
      (portfolioDataFetching && !getPortfolioDataQuery.isLoading),
    refetch,
    getNoCachePortfolioData,
  };
};

interface useNoExchangeConnectedModalProps {
  action?: string;
}

export const useNoExchangeConnectedModal = ({
  action,
}: useNoExchangeConnectedModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const workspace = useSelector(selectWorkspace);
  const isDemo = workspace === WorkspaceType.DEMO;

  return {
    title: isDemo
      ? t("no_exchange_connected.title_demo")
      : t("no_exchange_connected.title"),
    description: isDemo
      ? t("no_exchange_connected.description_demo", { action })
      : t("no_exchange_connected.description", { action }),
    buttonText: isDemo
      ? t("no_exchange_connected.button_demo")
      : t("no_exchange_connected.button"),
    isDemo,
  };
};

interface useAccountCardProps {
  account: PortfolioAccountData;
}

export const useAccountCard = ({ account }: useAccountCardProps) => {
  const coinsData = useSelector(selectPreprocessedPortfolioData);

  const accountCoins = useMemo(() => {
    return updateCoinRatios(
      coinsData
        .filter(
          (coin) =>
            coin.client_id === account.client_id && coin.total_in_usd > 0
        )
        .sort((a, b) => b.total_in_usd - a.total_in_usd)
    ).map((coin) => {
      return {
        ...coin,
        name: coin.symbol,
        value: toFixed(coin.total_ratio * 100, 2),
      };
    });
  }, [coinsData, account]);

  return {
    accountCoins,
  };
};
